import { constants, Text, TextLink } from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { kebabCase } from 'lodash';
import type { Tag } from 'pages/i/types';

const messages = defineMessages({
  a11yTitle: {
    defaultMessage: 'View products tagged with {tag}',
  },
});

interface OptimisedSEOTagProps {
  tag: Tag;
  tagItemKey: number;
  seoHidden: boolean;
}

export const OptimisedSEOTag: React.FC<OptimisedSEOTagProps> = ({
  tag,
  tagItemKey,
  seoHidden,
}) => {
  const intl = useIntl();
  const tagLink = tag.slug;
  const linkTitle = intl.formatMessage(messages.a11yTitle, {
    tag: tag.linkTitle,
  });

  if (!tagLink) return null;

  const kebabKey = kebabCase(tag.linkLabel);
  if (seoHidden) {
    return (
      <Text type="body2" muted>
        <TextLink
          data-testid="hidden-seo-link"
          key={`tag-item-${kebabKey}-${tagItemKey}`}
          // onAuxClick is to support chrome/firefox for middle mouse/4th/5th button clicks
          onAuxClick={(ev) => {
            if (ev.button === 1) {
              window.open(tagLink, '_blank');
            } else if (ev.button === 4) {
              window.history.go();
            } else if (ev.button === 5) {
              window.history.back();
            }
          }}
          onClick={(ev) => {
            if (
              ev.ctrlKey ||
              ev.shiftKey ||
              ev.metaKey ||
              (ev.button && ev.button === 1)
            ) {
              ev.preventDefault();
              window.open(tagLink, '_blank');
              return;
            }
            window.location.href = tagLink;
          }}
          size={constants.SMALL}
          title={linkTitle}
        >
          {tag.linkLabel}
        </TextLink>
      </Text>
    );
  }

  return (
    <Text type="body2" muted>
      <TextLink
        href={tagLink}
        key={`tag-item-${kebabKey}-${tagItemKey}`}
        size={constants.SMALL}
        title={linkTitle}
        data-testid="visible-seo-link"
      >
        {tag.linkLabel}
      </TextLink>
    </Text>
  );
};
